import HorizontalRule from '@tiptap/extension-horizontal-rule';
import { mergeAttributes, canInsertNode, isNodeSelection } from '@tiptap/core';
import { TextSelection, NodeSelection } from '@tiptap/pm/state';
import { resolveBlockIndentFromContext } from '../lib/post_block_indent.js';
import {
  normalizeHrColor,
  normalizeHrWidth,
  postEditorHrClassList,
  parseHrAttrsFromClassName,
} from '../lib/post_editor_constants.js';
import { selectionInsideBlockquote } from '../lib/pathstone_blockquote_content.js';

export const PostHorizontalRule = HorizontalRule.extend({
  addAttributes() {
    return {
      color: {
        default: '#000000',
        parseHTML: (element) => {
          const d = element.getAttribute('data-ps-hr-color');
          if (d != null && d !== '') return normalizeHrColor(d);
          const { color: fromClass } = parseHrAttrsFromClassName(element.className);
          return fromClass ? normalizeHrColor(fromClass) : '#000000';
        },
        renderHTML: (attributes) => ({
          'data-ps-hr-color': normalizeHrColor(attributes.color),
        }),
      },
      lineWidth: {
        default: '1',
        parseHTML: (element) => {
          const d = element.getAttribute('data-ps-hr-width');
          if (d != null && d !== '') return normalizeHrWidth(d);
          const { lineWidth: fromClass } = parseHrAttrsFromClassName(element.className);
          return fromClass ? normalizeHrWidth(fromClass) : '1';
        },
        renderHTML: (attributes) => ({
          'data-ps-hr-width': normalizeHrWidth(attributes.lineWidth),
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'hr' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    // HTMLAttributes only contains per-attribute renderHTML output (data-ps-*),
    // not `color` / `lineWidth` keys — reading those was always undefined and
    // the merge below overwrote correct data-* with defaults.
    const color = normalizeHrColor(node.attrs.color);
    const lineWidth = normalizeHrWidth(node.attrs.lineWidth);
    return [
      'hr',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: postEditorHrClassList(color, lineWidth),
      }),
    ];
  },

  addCommands() {
    return {
      setHorizontalRule:
        (attrs = {}) =>
        ({ chain, state }) => {
          if (selectionInsideBlockquote(state)) return false;
          if (!canInsertNode(state, state.schema.nodes[this.name])) {
            return false;
          }
          const nodeAttrs = {
            color: normalizeHrColor(attrs.color),
            lineWidth: normalizeHrWidth(attrs.lineWidth),
            indent: resolveBlockIndentFromContext(state),
          };
          const { selection } = state;
          const { $to: $originTo } = selection;
          const currentChain = chain();
          if (isNodeSelection(selection)) {
            currentChain.insertContentAt($originTo.pos, {
              type: this.name,
              attrs: nodeAttrs,
            });
          } else {
            currentChain.insertContent({ type: this.name, attrs: nodeAttrs });
          }
          return currentChain
            .command(({ state: chainState, tr, dispatch }) => {
              if (dispatch) {
                const { $to } = tr.selection;
                const posAfter = $to.end();
                if ($to.nodeAfter) {
                  if ($to.nodeAfter.isTextblock) {
                    tr.setSelection(TextSelection.create(tr.doc, $to.pos + 1));
                  } else if ($to.nodeAfter.isBlock) {
                    tr.setSelection(NodeSelection.create(tr.doc, $to.pos));
                  } else {
                    tr.setSelection(TextSelection.create(tr.doc, $to.pos));
                  }
                } else {
                  const nodeType =
                    chainState.schema.nodes[this.options.nextNodeType] ||
                    $to.parent.type.contentMatch.defaultType;
                  const node = nodeType?.create();
                  if (node) {
                    tr.insert(posAfter, node);
                    tr.setSelection(TextSelection.create(tr.doc, posAfter + 1));
                  }
                }
                tr.scrollIntoView();
              }
              return true;
            })
            .run();
        },
    };
  },
});
